import * as XLSX from 'xlsx'
import { toOverrideCheckType } from '../../../lib/verification/overrides'
import type { CheckOverride, VerificationResult } from '../../../lib/verification/types'

export interface BatchRowResult {
  result: VerificationResult
  overrides: CheckOverride[]
}

function overallStatusLabel(result: VerificationResult): string {
  if (result.noInvoicesFound) return 'NO CI DATA'
  if (result.failCount === 0) return result.naCount > 0 ? `ALL PASS (${result.naCount} N/A)` : 'ALL PASS'
  return `${result.failCount} OF ${result.passCount + result.failCount} FAILED`
}

export function buildVerificationWorkbook(rows: BatchRowResult[]): XLSX.WorkBook {
  const summaryHeaders = ['Job Ref', 'CI File', 'Overall Status', 'Pass', 'Fail', 'N/A', 'Consol Bundle']
  const summaryRows = rows.map(({ result }) => [
    result.jobRef,
    result.cisIncluded.map(c => c.fileName).join(', ') || '',
    overallStatusLabel(result),
    result.passCount,
    result.failCount,
    result.naCount,
    result.consol ? `parent ${result.consol.parentJobRef} + children ${result.consol.childJobRefs.join(', ')}` : '',
  ])
  const summarySheet = XLSX.utils.aoa_to_sheet([summaryHeaders, ...summaryRows])

  // Auto-check result first (audit trail), override decision alongside it (final decision).
  // Task T: Shipper/Importer Match are now 4 separate checks (name/address split per
  // party), each with its own simple Job Value/CI Value/Status row -- no more combined
  // name+address columns needed here.
  const detailHeaders = ['Job Ref', 'Check', 'Item', 'Job Value', 'CI Value', 'Auto Status', 'Note', 'Override', 'Overridden By', 'Override Reason']
  const detailRows: (string | number)[][] = []
  for (const { result, overrides } of rows) {
    const overrideByCheckType = new Map(overrides.map(o => [o.checkType, o]))
    for (const check of result.checks) {
      const override = overrideByCheckType.get(toOverrideCheckType(check.id))
      const overrideCols = [
        override ? override.overrideStatus.toUpperCase() : '',
        override ? `${override.staffEmail} (${new Date(override.createdAt).toLocaleString()})` : '',
        override ? override.notes : '',
      ]
      if (check.id === 'totals') {
        detailRows.push([result.jobRef, 'Totals', 'Packages', check.packages.jobValue, check.packages.ciValue, check.packages.status.toUpperCase(), '', ...overrideCols])
        detailRows.push([result.jobRef, 'Totals', 'Gross Weight', check.grossWeight.jobValue, check.grossWeight.ciValue, check.grossWeight.status.toUpperCase(), '', ...overrideCols])
        detailRows.push([result.jobRef, 'Totals', 'CBM', check.cbm.jobValue ?? '', check.cbm.ciValue ?? '', check.cbm.status.toUpperCase(), check.cbm.note ?? '', ...overrideCols])
      } else {
        for (const row of check.rows) {
          detailRows.push([result.jobRef, check.title, row.label, row.jobValue ?? '', row.ciValue ?? '', row.status.toUpperCase(), row.note ?? '', ...overrideCols])
        }
      }
    }
  }
  const detailSheet = XLSX.utils.aoa_to_sheet([detailHeaders, ...detailRows])

  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, summarySheet, 'Batch Summary')
  XLSX.utils.book_append_sheet(wb, detailSheet, 'Details')

  const anyOverrides = rows.some(r => r.overrides.length > 0)
  if (anyOverrides) {
    const overrideHeaders = ['Job Ref', 'Check', 'Auto Result', 'Override Decision', 'Staff', 'Date', 'Reason']
    const overrideRows: (string | number)[][] = []
    for (const { result, overrides } of rows) {
      const overrideByCheckType = new Map(overrides.map(o => [o.checkType, o]))
      for (const check of result.checks) {
        const override = overrideByCheckType.get(toOverrideCheckType(check.id))
        if (!override) continue
        overrideRows.push([
          result.jobRef,
          check.title,
          check.status.toUpperCase(),
          override.overrideStatus.toUpperCase(),
          override.staffEmail,
          new Date(override.createdAt).toLocaleString(),
          override.notes,
        ])
      }
    }
    const overrideSheet = XLSX.utils.aoa_to_sheet([overrideHeaders, ...overrideRows])
    XLSX.utils.book_append_sheet(wb, overrideSheet, 'Overrides')
  }

  return wb
}

export function downloadVerificationExcel(rows: BatchRowResult[]): void {
  const wb = buildVerificationWorkbook(rows)
  const jobRefs = rows.map(r => r.result.jobRef).join('_')
  XLSX.writeFile(wb, `verification_batch_${jobRefs || Date.now()}.xlsx`)
}

export function verificationWorkbookToBlob(rows: BatchRowResult[]): Blob {
  const wb = buildVerificationWorkbook(rows)
  const buffer = XLSX.write(wb, { type: 'array', bookType: 'xlsx' })
  return new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
}
